import React, { useEffect, useState, useRef, useMemo, useCallback } from 'react';
import '../styles/ViewerScreen.css';
import { ViewerWebRtcManager, ScoreboardPayload, ConnectionStatus } from '../utils/webRtcManager';
import config from '../config';

const SimpleQRCode: React.FC<{ value: string; size?: number }> = ({ value, size = 200 }) => {
  const qrUrl = `https://api.qrserver.com/v1/create-qr-code/?size=${size}x${size}&data=${encodeURIComponent(value)}&margin=1`;
  
  return (
    <img 
      src={qrUrl} 
      alt="Pairing QR Code" 
      width={size} 
      height={size}
      style={{ display: 'block', borderRadius: '8px' }}
      loading="eager"
    />
  );
};

export const ViewerScreen: React.FC = () => {
  // Koppelcode & Sessie-ID (4 cijfers)
  const [sessionId, setSessionId] = useState<string>(() => {
    let stored = sessionStorage.getItem('squash_viewer_session');
    if (!stored) {
      stored = Math.floor(1000 + Math.random() * 9000).toString();
      sessionStorage.setItem('squash_viewer_session', stored);
    }
    return stored;
  });

  const [connectionStatus, setConnectionStatus] = useState<ConnectionStatus>('disconnected');
  const [showPairingModal, setShowPairingModal] = useState<boolean>(true);
  const [isFullscreen, setIsFullscreen] = useState<boolean>(false);

  // Forceer verversen en ontkoppel service worker caches
  const handleForceRefresh = () => {
    if ('serviceWorker' in navigator) {
      navigator.serviceWorker.getRegistrations().then(regs => {
        for (const reg of regs) {
          reg.unregister();
        }
      });
    }
    // Hard reload
    window.location.reload();
  };

  // Luister naar Service Worker updates en ververs automatisch
  useEffect(() => {
    const handleSwUpdate = (e: any) => {
      console.log('[Viewer] Nieuwe app-versie beschikbaar! Verversen...');
      if (e.detail?.waiting) {
        e.detail.waiting.postMessage({ type: 'SKIP_WAITING' });
      }
      window.location.reload();
    };
    window.addEventListener('sw-update-available', handleSwUpdate);
    return () => window.removeEventListener('sw-update-available', handleSwUpdate);
  }, []);
  const [showLogsDrawer, setShowLogsDrawer] = useState<boolean>(false);
  const [viewerLogs, setViewerLogs] = useState<string[]>([]);

  const addViewerLog = useCallback((msg: string) => {
    setViewerLogs(prev => [...prev.slice(-9), msg]);
  }, []);

  // Nieuwe 4-cijferige sessiecode genereren
  const handleGenerateNewCode = () => {
    const newCode = Math.floor(1000 + Math.random() * 9000).toString();
    sessionStorage.setItem('squash_viewer_session', newCode);
    setSessionId(newCode);
    addViewerLog(`Nieuwe code aangemaakt: ${newCode}`);
    if (rtcManagerRef.current) {
      rtcManagerRef.current.setSessionId(newCode);
    }
  };

  // Volledig scherm toggle (verbergt adresbalk in browsers die Fullscreen API ondersteunen)
  const toggleFullscreen = () => {
    const doc = document as any;
    if (!doc.fullscreenElement && !doc.webkitFullscreenElement) {
      const el = document.documentElement as any;
      if (el.requestFullscreen) {
        el.requestFullscreen().catch((e: any) => console.debug('Fullscreen error:', e));
      } else if (el.webkitRequestFullscreen) {
        el.webkitRequestFullscreen();
      }
    } else {
      if (doc.exitFullscreen) {
        doc.exitFullscreen().catch((e: any) => console.debug('Exit fullscreen error:', e));
      } else if (doc.webkitExitFullscreen) {
        doc.webkitExitFullscreen();
      }
    }
  };

  useEffect(() => {
    const onFullscreenChange = () => {
      const doc = document as any;
      setIsFullscreen(!!(doc.fullscreenElement || doc.webkitFullscreenElement));
    };
    document.addEventListener('fullscreenchange', onFullscreenChange);
    document.addEventListener('webkitfullscreenchange', onFullscreenChange);
    return () => {
      document.removeEventListener('fullscreenchange', onFullscreenChange);
      document.removeEventListener('webkitfullscreenchange', onFullscreenChange);
    };
  }, []);

  // Scoreboard State
  const [scoreData, setScoreData] = useState<ScoreboardPayload>({
    type: 'SCORE_UPDATE',
    player1: {
      name: 'Speler 1',
      score: 0,
      gamesWon: 0,
      isServing: true,
      pastGames: []
    },
    player2: {
      name: 'Speler 2',
      score: 0,
      gamesWon: 0,
      isServing: false,
      pastGames: []
    },
    server: 1,
    serviceBox: 'right',
    gameNumber: 1,
    matchStarted: false,
    timestamp: Date.now()
  });

  const rtcManagerRef = useRef<ViewerWebRtcManager | null>(null);

  useEffect(() => {
    let wakeLock: any = null;
    const requestWakeLock = async () => {
      try {
        if ('wakeLock' in navigator) {
          wakeLock = await (navigator as any).wakeLock.request('screen');
          console.log('[Viewer] Screen WakeLock actief');
        }
      } catch (err) {
        console.warn('[Viewer] WakeLock kon niet worden geactiveerd:', err);
      }
    };
    requestWakeLock();

    // Start WebRTC Viewer Manager
    const manager = new ViewerWebRtcManager(
      sessionId,
      (status) => {
        setConnectionStatus(status);
        if (status === 'connected') {
          setShowPairingModal(false);
        }
      },
      (payload) => {
        console.log('[Viewer] Realtime score ontvangen:', payload);
        setScoreData(payload);
        setShowPairingModal(false);
      },
      (msg) => {
        addViewerLog(msg);
      }
    );

    rtcManagerRef.current = manager;
    manager.init(config.wsUrl);

    return () => {
      manager.destroy();
      if (wakeLock) {
        wakeLock.release().catch((e: any) => console.debug(e));
      }
    };
  }, [sessionId, addViewerLog]);

  const p1 = scoreData.player1;
  const p2 = scoreData.player2;

  const formatScore = (score: number) => {
    return (score ?? 0).toString();
  };

  const qrPairingPayload = `https://squash.aiqagents.nl/?pair=${sessionId}`;

  return (
    <div className="viewer-screen-root">
      {/* Top Controls Bar rechtsboven: Fullscreen, Logs & Status Badge */}
      <div className="viewer-top-bar">
        <button 
          className="viewer-action-btn"
          onClick={handleForceRefresh}
          title="Pagina verversen en cache legen"
        >
          🔄 Herlaad
        </button>

        <button 
          className="viewer-action-btn"
          onClick={() => setShowLogsDrawer(prev => !prev)}
          title="Verbindingslogs tonen/verbergen"
        >
          📋 Logs ({viewerLogs.length})
        </button>

        <button 
          className="viewer-action-btn"
          onClick={toggleFullscreen}
          title={isFullscreen ? "Fullscreen verlaten" : "Volledig scherm openen (verbergt browserbalk)"}
        >
          {isFullscreen ? '⛶ Verkleinen' : '⛶ Volledig scherm'}
        </button>

        <div 
          className="viewer-status-badge" 
          onClick={() => setShowPairingModal(prev => !prev)}
          title="Klik om koppel-scherm te openen of sluiten"
        >
          <span className={`viewer-status-dot ${connectionStatus}`} />
          <span>{connectionStatus === 'connected' ? 'LIVE Verbonden' : `Koppelcode: ${sessionId}`}</span>
        </div>
      </div>

      {/* Scoreboard Grid (50/50 split landscape) */}
      <div className="scoreboard-container">
        {/* SPELER 1 (LINKS) */}
        <div className={`player-card player-left ${p1.isServing ? 'serving' : ''}`}>
          <div className="player-header">
            <h1 className="player-name">{p1.name || 'Speler 1'}</h1>
            <div className="set-scores">
              {p1.pastGames && p1.pastGames.map((score, idx) => (
                <span key={idx} className="set-pill past-set">{score}</span>
              ))}
              <span className="set-pill current-sets-won">{p1.gamesWon}</span>
            </div>
          </div>
          <div className="score-block">
            <span className={`live-score ${formatScore(p1.score).length > 1 ? "two-digits" : "single-digit"}`}>{formatScore(p1.score)}</span>
          </div>
        </div>

        {/* SPELER 2 (RECHTS) */}
        <div className={`player-card player-right ${p2.isServing ? 'serving' : ''}`}>
          <div className="player-header">
            <h1 className="player-name">{p2.name || 'Speler 2'}</h1>
            <div className="set-scores">
              {p2.pastGames && p2.pastGames.map((score, idx) => (
                <span key={idx} className="set-pill past-set">{score}</span>
              ))}
              <span className="set-pill current-sets-won">{p2.gamesWon}</span>
            </div>
          </div>
          <div className="score-block">
            <span className={`live-score ${formatScore(p2.score).length > 1 ? "two-digits" : "single-digit"}`}>{formatScore(p2.score)}</span>
          </div>
        </div>
      </div>

      {/* Losse Log Drawer (rechtsboven oproepbaar) */}
      {showLogsDrawer && (
        <div className="viewer-logs-overlay-drawer">
          <div className="viewer-logs-drawer-header">
            <h4>📋 Verbindingsactiviteit (Live)</h4>
            <button onClick={() => setShowLogsDrawer(false)}>✕</button>
          </div>
          <div className="viewer-logs-drawer-content">
            {viewerLogs.length === 0 ? (
              <div className="viewer-log-empty">Nog geen activiteit gelogd</div>
            ) : (
              viewerLogs.map((l, idx) => (
                <div key={idx} className="viewer-log-line">{l}</div>
              ))
            )}
          </div>
        </div>
      )}

      {/* Koppel-Overlay met QR-code (verdwijnt automatisch na verbinden of via sluitknop) */}
      {showPairingModal && (
        <div className="viewer-pairing-overlay" onClick={() => setShowPairingModal(false)}>
          <div className="viewer-pairing-card" style={{ position: 'relative' }} onClick={e => e.stopPropagation()}>
            <button 
              onClick={() => setShowPairingModal(false)}
              title="Sluiten"
              style={{
                position: 'absolute',
                top: '12px',
                right: '16px',
                background: 'transparent',
                border: 'none',
                color: '#94a3b8',
                fontSize: '24px',
                cursor: 'pointer',
                padding: '4px 8px',
                lineHeight: 1
              }}
            >
              ✕
            </button>
            <h2>Toeschouwersscherm</h2>
            <p>Scan de QR-code met je telefoon, of vul de koppelcode in via het 📺 VIEWER menu:</p>
            
            <div className="viewer-qr-wrapper">
              <SimpleQRCode value={qrPairingPayload} size={200} />
            </div>

            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px', flexWrap: 'wrap' }}>
              <div className="viewer-pin-code">{sessionId}</div>
              <button 
                className="viewer-action-btn"
                style={{ height: '42px', borderRadius: '10px' }}
                onClick={handleGenerateNewCode}
                title="Genereer een nieuwe 4-cijferige code"
              >
                🔄 Nieuwe code
              </button>
              <button 
                className="viewer-action-btn"
                style={{ height: '42px', borderRadius: '10px' }}
                onClick={handleForceRefresh}
                title="Ververs de pagina en leeg de browsercache"
              >
                🔄 Herlaad pagina
              </button>
            </div>

            <div className={`viewer-status-text ${connectionStatus === 'connected' ? 'connected' : ''}`}>
              {connectionStatus === 'connected' && '✓ Verbonden met teller (Live Score Actief)'}
              {connectionStatus === 'connecting' && 'Verbinden met server & wachten op teller...'}
              {connectionStatus === 'disconnected' && 'Wachten op de teller... (verbind tablet met hotspot telefoon)'}
              {connectionStatus === 'error' && '⚠️ Netwerkfout — controleer internet of hotspot'}
            </div>

            {/* iPad / Safari Fullscreen Instructie */}
            <div className="viewer-ipad-tip">
              💡 <strong>iPad tip:</strong> Tik in Safari op Deel (vierkant met pijl omhoog) en kies <strong>&quot;Zet op beginscherm&quot;</strong>. Zo start de viewer altijd als echte app zónder adresbalk!
            </div>

            {/* Live Diagnostiek Logbox binnen modal */}
            <div className="viewer-modal-log-section">
              <div className="viewer-log-title">📋 Live activiteit:</div>
              <div className="viewer-log-entries">
                {viewerLogs.length === 0 ? (
                  <div className="viewer-log-empty">Wachten op initiële verbinding...</div>
                ) : (
                  viewerLogs.slice(-5).map((l, idx) => (
                    <div key={idx} className="viewer-log-line">{l}</div>
                  ))
                )}
              </div>
            </div>

            <div style={{ marginTop: '16px', display: 'flex', gap: '10px', justifyContent: 'center' }}>
              <button 
                style={{
                  background: connectionStatus === 'connected' ? '#10b981' : '#334155',
                  color: '#fff',
                  border: 'none',
                  padding: '10px 24px',
                  borderRadius: '8px',
                  fontWeight: 'bold',
                  cursor: 'pointer'
                }}
                onClick={() => setShowPairingModal(false)}
              >
                {connectionStatus === 'connected' ? 'Scorebord tonen' : 'Scorebord bekijken'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ViewerScreen;
